import React, { useRef, useState } from 'react';
import Swal from 'sweetalert2';
import { CognitoUser } from 'amazon-cognito-identity-js';
import {
  AuthStep,
  FormSubmitHandler,
  type CodeFormData,
  type KeyDownHandler,
} from '../types';
import { confirmCode, getUserAccess, signIn, AuthState } from '../AuthState';
import { useAuthStep } from '../context/AuthStepContext';
import { useNavigate } from 'react-router-dom';

const useEmailVerification = (onSubmit?: (data: CodeFormData) => void) => {
  const navigate = useNavigate();
  const codeRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const [emailVerifyCode, setEmailVerifyCode] = useState<string>('');
  const {
    nextStep,
    pin,
    skipTo,
    resetStep,
    setIsSignInFlow,
    setHasAccount,
    email,
  } = useAuthStep();
  const [isLoading, setIsLoading] = React.useState(false);

  const emailCodeUpdated = async (e: string) => {
    setEmailVerifyCode(e);
    const codeProvided = pin.length === 4;
    if (e.length === 6) {
      AuthState.emailCode = e;
      if (!codeProvided) {
        nextStep();
        return;
      }
      setIsLoading(true);
      const formattedPin = pin.padEnd(6, '0');
      const finalCode = codeProvided ? e : e + formattedPin;
      const user: CognitoUser = await confirmCode(finalCode);
      if (user && user.getSignInUserSession()) {
        const userAccess = await getUserAccess();
        if (!userAccess.registered) {
          nextStep();
          setEmailVerifyCode('');
        } else {
          if (userAccess.userStatus === 1) {
            window.location.href = '/';
            return;
          } else {
            setIsSignInFlow(false);
            setHasAccount(true);
            setIsLoading(false);
            skipTo(AuthStep.COMPLETE);
          }
        }
      } else if (user && !user.getSignInUserSession()) {
        const Toast = Swal.mixin({
          toast: true,
          position: 'bottom',
          timer: 3000,
          timerProgressBar: true,
        });
        Toast.fire(`Incorrect code.`, '', 'info');
        setEmailVerifyCode('');
      } else {
        Swal.fire({
          icon: 'error',
          text: 'Maximum tries exceeded or code timed out. Please try again.',
          showConfirmButton: true,
        });
        setEmailVerifyCode('');
        resetStep();
      }
    }
  };

  const handleSubmit: FormSubmitHandler = (e) => {
    e.preventDefault();
    const data = {
      code: codeRef.current?.value || '',
    };
    if (onSubmit) {
      onSubmit(data);
    }
  };

  const handleEmailKeyDown: KeyDownHandler = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleEmailSubmit();
    }
  };

  const handleEmailSubmit = async (): Promise<void> => {
    if (emailRef.current) {
      AuthState.email = emailRef.current.value;
    }
    AuthState.isRegistering = true;
    setIsLoading(true);

    AuthState.cognitoUser = await signIn(
      AuthState.email,
      AuthState.phone,
      AuthState.pin,
    );

    setIsLoading(false);
    setEmailVerifyCode('');
    navigate('/verify');
  };

  return {
    email,
    navigate,
    codeRef,
    emailRef,
    isLoading,
    emailCodeUpdated,
    handleSubmit,
    handleEmailKeyDown,
    handleEmailSubmit,
    emailVerifyCode,
  };
};

export { useEmailVerification };
